// Fog of war, as SudokuPad does it: the cells in `fog.visible` are lit from the start. Without
// triggers, every correct digit the player places lights the 3×3 around it. With triggers
// (dynamic fog), only the triggers light anything: once every cell a trigger watches holds its
// correct digit, its cells light. Givens light nothing by themselves. Around a grid smaller than
// its canvas, the margin (where outside clues sit) is fogged and lit the same way.
//
// A given still in the fog is an empty cell to the player: nothing may tell them it's there (Marko,
// 2026-11-02). They can mark it and type in it, and a correct digit there clears like any other.
// Once lit, the given shows over whatever they typed.
import type { PuzzleDoc, Rc } from '@zinc/shared';
import { cellAt, type Geometry } from './geometry';

const key = (r: number, c: number) => `${r},${c}`;

/**
 * Lit squares of the canvas ("r,c"), cells and margin alike. `entered` is the digit the player
 * typed per cell (never a given's), or null for the starting light alone (no solution needed).
 */
function litSquares(
  g: Geometry,
  fog: NonNullable<PuzzleDoc['fog']>,
  entered: readonly (string | null)[] | null,
): Set<string> {
  const lit = new Set<string>();
  const onCanvas = (r: number, c: number) => r >= 0 && c >= 0 && r < g.rows && c < g.cols;
  for (const [r, c] of fog.visible ?? []) if (onCanvas(r, c)) lit.add(key(r, c));
  const solution = g.solution;
  if (!entered || !solution) return lit;
  const placed = (i: number) => entered[i] != null && entered[i] === solution[i];
  if (fog.triggers?.length) {
    for (const t of fog.triggers) {
      const watched = t.when.map(([r, c]) => cellAt(g, r, c));
      if (watched.some((i) => i < 0)) continue;
      if (!watched.every((i) => placed(i) || g.givens[i] !== null)) continue;
      if (!watched.some(placed)) continue;
      for (const [r, c] of t.light) if (onCanvas(r, c)) lit.add(key(r, c));
    }
    return lit;
  }
  g.cells.forEach(([r, c], i) => {
    if (!placed(i)) return;
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (onCanvas(r + dr, c + dc)) lit.add(key(r + dr, c + dc));
      }
    }
  });
  return lit;
}

function cellsOf(g: Geometry, lit: Set<string>): Set<number> {
  const out = new Set<number>();
  g.cells.forEach(([r, c], i) => {
    if (lit.has(key(r, c))) out.add(i);
  });
  return out;
}

/**
 * The cells lit before any digit is placed, or null when the document has no fog. Needs no
 * solution, so thumbnails (drawn from documents without one) keep the fog too.
 */
export function startingLight(g: Geometry, fog: PuzzleDoc['fog']): Set<number> | null {
  if (!fog) return null;
  return cellsOf(g, litSquares(g, fog, null));
}

/**
 * Lit cells (indices in core order), or null when the fog doesn't apply: no fog in the
 * document, or no solution to tell correct digits from wrong ones (then everything shows).
 * `entered` is the digit the player typed per cell (null where empty). A given counts as correct
 * in a trigger that watches a group of cells, and lights nothing by itself.
 */
export function litCells(
  g: Geometry,
  fog: PuzzleDoc['fog'],
  entered: readonly (string | null)[],
): Set<number> | null {
  if (!fog || !g.solution) return null;
  return cellsOf(g, litSquares(g, fog, entered));
}

/** The givens the fog still hides: to the player, their cells are empty. */
export function hiddenGivens(g: Geometry, entered: readonly (string | null)[]): Set<number> {
  const out = new Set<number>();
  const lit = litCells(g, g.fog, entered);
  if (!lit) return out;
  g.givens.forEach((v, i) => {
    if (v !== null && !lit.has(i)) out.add(i);
  });
  return out;
}

/**
 * The margin around a grid smaller than its canvas: the canvas squares that aren't cells, when
 * the cells fill a rectangle (a 9×9 in a 10×10 or 11×11 canvas, with clues around it). Empty
 * for other grids, so the gaps of a samurai aren't fogged.
 */
export function marginSquares(g: Geometry): Rc[] {
  if (g.cells.length === 0 || g.cells.length === g.rows * g.cols) return [];
  const rows = g.cells.map((c) => c[0]);
  const cols = g.cells.map((c) => c[1]);
  const h = Math.max(...rows) - Math.min(...rows) + 1;
  const w = Math.max(...cols) - Math.min(...cols) + 1;
  if (h * w !== g.cells.length) return [];
  const out: Rc[] = [];
  for (let r = 0; r < g.rows; r++) {
    for (let c = 0; c < g.cols; c++) if (cellAt(g, r, c) < 0) out.push([r, c]);
  }
  return out;
}

/**
 * Fogged margin squares, or null when the fog doesn't apply (as for `litCells`; with
 * `starting`, the fog before any digit, which needs no solution).
 */
export function foggedMargin(
  g: Geometry,
  fog: PuzzleDoc['fog'],
  entered: readonly (string | null)[],
  starting = false,
): Rc[] | null {
  if (!fog || (!starting && !g.solution)) return null;
  const margin = marginSquares(g);
  if (margin.length === 0) return [];
  const lit = litSquares(g, fog, starting ? null : entered);
  return margin.filter(([r, c]) => !lit.has(key(r, c)));
}
